/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';

import template from './device-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {DeviceApiService} from '../device-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-device-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.device;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  vm: any[] = [];
  vmIndex = -1;
  readonly resolved = signal(false);
  readonly deviceProperties = signal<any>({
    targetedDeviceTypes: [],
    targetedOSes: [],
    targetedBrands: []
  });

  private readonly emptyModel = {
    targetedDeviceTypes: [],
    targetedOSes: [],
    targetedBrands: []
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summaryData = TargetingHelper.getSummary(this.model, ADN_TARGETING_TYPES.device) || {};
      const summaryString = _.map(summaryData, (values: string[], prop: string) => {
        return this.translate.instant('targeting.device.' + prop) + ': ' + this.formatSelections(values);
      }).join('\n');
      return {
        vm: _.cloneDeep(this.vm),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.device,
        summary: summaryString
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly deviceApi: DeviceApiService,
    private readonly translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.init();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
    this.tabController.register(ADN_TARGETING_TYPES.device.widget, () => this.loadDevices(), copyTemplate);
  }

  private loadDevices(): void {
    if (this.resolved()) {
      return;
    }
    this.deviceApi.getProperties().subscribe({
      next: (deviceProperties) => {
        this.deviceProperties.set(deviceProperties);
        this.resolved.set(true);
      },
      error: () => {
        this.resolved.set(true);
      }
    });
  }

  edit(editIndex: number): void {
    this.vmIndex = editIndex;
  }

  up(): void {
    this.vmIndex = -1;
  }

  add(): void {
    this.addNewDeviceModel();
  }

  remove(index: number): void {
    _.pullAt(this.vm, index);
    if (this.vmIndex >= index) {
      this.vmIndex--;
    }
    this.sanityCheckVmIndex();
  }

  formatSelections(selections: string[]): string {
    return _.map(selections || [], (selection) => {
      const key = 'targeting.device.' + selection;
      const tx = this.translate.instant(key);
      return tx === key ? selection : tx;
    }).join(', ');
  }

  isSelected(list: string[], value: string): boolean {
    return (list || []).indexOf(value) > -1;
  }

  toggleSelection(list: string[], value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const arr = list || [];
    const index = arr.indexOf(value);
    if (checked && index < 0) {
      arr.push(value);
    } else if (!checked && index > -1) {
      arr.splice(index, 1);
    }
  }

  private addNewDeviceModel(): void {
    this.vm.push(_.cloneDeep(this.emptyModel));
    this.vmIndex = this.vm.length - 1;
  }

  private sanityCheckVmIndex(): void {
    if (!_.isFinite(this.vmIndex) || this.vmIndex > (this.vm.length - 1)) {
      this.vmIndex = -1;
    }
  }

  private init(): void {
    this.vm = [];
    _.forEach(this.model || [], (entryObj: any) => {
      this.vm.push(entryObj);
    });
    if (this.vm.length === 0) {
      this.addNewDeviceModel();
    }
    this.sanityCheckVmIndex();
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.vm, (entryObj: any) => {
      if (this.arrSafe(_.get(entryObj, 'targetedDeviceTypes')) ||
        this.arrSafe(_.get(entryObj, 'targetedOSes')) ||
        this.arrSafe(_.get(entryObj, 'targetedBrands'))) {
        this.model.push(entryObj);
      }
    });
    this.init();
  }

  private arrSafe(data: any): boolean {
    return _.isArray(data) && data.length > 0;
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.init();
  }
}
